import { motion } from "framer-motion";
import { Download, FileText, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Palyazatok() {
  return (
    <div data-testid="palyazatok-page" className="bg-[#F8FAFC]">
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 bg-gradient-to-br from-[#0B192C] via-[#102542] to-[#1E3E62] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,#fff_1px,transparent_0)] [background-size:24px_24px]" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#EA580C]/15 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-semibold tracking-widest uppercase"
          >
            <Award className="w-3.5 h-3.5 text-[#EA580C]" />
            Pályázatok
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight"
          >
            Sikeres pályázataink
          </motion.h1>
          <p className="mt-4 text-gray-300 max-w-2xl">
            A Fortuna Ponyva Kft. büszkén tájékoztatja partnereit és
            látogatóit az Európai Unió és a Magyar Kormány által
            társfinanszírozott, sikeresen elnyert pályázatáról.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 space-y-10">
          {/* Project card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="grid md:grid-cols-2">
              {/* Left: image / branding */}
              <div className="bg-gradient-to-br from-[#0B192C] to-[#1E3E62] p-8 md:p-10 flex flex-col justify-between gap-6">
                <div className="space-y-4">
                  <img
                    src="/assets/palyazat/palyazat-1.jpg"
                    alt="Pályázati tábla – Fortuna Ponyva Kft."
                    className="w-full h-auto rounded-md shadow-2xl border border-white/10 bg-white"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <a
                  href="/assets/palyazat/palyazati-tabla.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-white/90 hover:text-white transition-colors self-start"
                >
                  <Download className="w-4 h-4" />
                  Pályázati tábla letöltése (PDF)
                </a>
              </div>

              {/* Right: details */}
              <div className="p-8 md:p-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EA580C]/10 text-[#EA580C] text-xs font-semibold tracking-widest uppercase">
                  <FileText className="w-3.5 h-3.5" />
                  GINOP Plusz
                </div>

                <h2 className="mt-4 font-heading font-bold text-[#0B192C] text-2xl md:text-3xl leading-tight">
                  Munkakörülmények fejlesztése II.
                </h2>

                <dl className="mt-6 space-y-4 text-sm">
                  <div>
                    <dt className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                      Projekt azonosító
                    </dt>
                    <dd className="mt-1 font-mono-tech font-semibold text-[#0B192C]">
                      GINOP_PLUSZ-3.2.5-24-2024-00001
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                      Kedvezményezett
                    </dt>
                    <dd className="mt-1 text-[#0B192C]">
                      Fortuna Ponyva Gyártó, Kereskedelmi és Szolgáltató Kft.
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                      Vissza nem térítendő támogatás összege
                    </dt>
                    <dd className="mt-1 font-heading font-bold text-[#EA580C] text-2xl">
                      7,87 millió Ft
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                      Operatív program
                    </dt>
                    <dd className="mt-1 text-[#0B192C]">
                      Gazdaságfejlesztési és Innovációs Operatív Program Plusz
                      (GINOP Plusz)
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                      Finanszírozás
                    </dt>
                    <dd className="mt-1 text-[#0B192C]">
                      Az Európai Unió társfinanszírozásával és a Magyar
                      Kormány támogatásával – Széchenyi Terv Plusz keretében.
                    </dd>
                  </div>
                </dl>

                <div className="mt-8 p-4 rounded-md bg-[#F8FAFC] border-l-4 border-[#EA580C]">
                  <p className="text-sm font-heading font-semibold text-[#0B192C]">
                    „Köszönjük a Magyar Kormány és az Európai Unió támogatását!"
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Info note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8"
          >
            <h3 className="font-heading font-bold text-[#0B192C] text-lg">
              Kötelező tájékoztatás
            </h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              A projekt megvalósítására és a támogatás felhasználására
              vonatkozó, jogszabályban előírt tájékoztatás keretében
              közzétett információk. További információért kérjük vegye fel
              velünk a kapcsolatot.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
